/**
 * Carries what a visitor picked before opening the music generator (a song
 * idea, a template, or lyrics they wrote) through sign-up. It waits in this
 * browser's localStorage, which survives the login page and OAuth
 * redirects, and is taken out once by /music-generator, which fills the form
 * with it. Nothing is generated automatically: the visitor still presses
 * Generate.
 *
 * Never throws: when storage is unavailable (private windows), the visitor
 * just starts from an empty form.
 */

const KEY = 'ep-music-handoff';
/** A hand-off older than this is dropped instead of used. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_PROMPT = 400;
/** The generator's paid limits; it cuts to the visitor's plan on arrival. */
const MAX_LYRICS = 3000;
const MAX_STYLE = 1000;

export interface MusicHandoff {
  /** Song description for description mode. */
  prompt?: string;
  /** A template id from @/constants/musicPresets. */
  templateId?: string;
  /** Lyrics for Custom Mode. */
  lyrics?: string;
  /** Style for Custom Mode (with lyrics). */
  style?: string;
  instrumental?: boolean;
  /** Open in Custom Mode (templates fill Style there). */
  custom?: boolean;
  /** Where it came from (a landing page path or a template), for analytics. */
  source?: string;
  savedAt: number;
}

const clean = (value: string | undefined, max: number) =>
  value?.trim().slice(0, max) || undefined;

export function saveMusicHandoff(
  value: Omit<MusicHandoff, 'savedAt'>,
): boolean {
  try {
    const record: MusicHandoff = {
      ...value,
      prompt: clean(value.prompt, MAX_PROMPT),
      lyrics: clean(value.lyrics, MAX_LYRICS),
      style: clean(value.style, MAX_STYLE),
      savedAt: Date.now(),
    };
    window.localStorage.setItem(KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

/** Returns the pending hand-off and removes it, so it is used once. */
export function takeMusicHandoff(): MusicHandoff | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    window.localStorage.removeItem(KEY);
    const record = JSON.parse(raw) as MusicHandoff;
    if (!record || typeof record.savedAt !== 'number') return null;
    if (Date.now() - record.savedAt > MAX_AGE_MS) return null;
    if (!record.prompt && !record.templateId && !record.lyrics) return null;
    return record;
  } catch {
    return null;
  }
}
